import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';

import { convertFromESTToLocal } from '@/utils/date/temporary';
import { exportLossAndDamageClaimsToExcel } from '@/utils/excel';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import { LAD_CLAIM_EXPORT_FIELDS } from '../../../../../contants/ClaimsEnums';
import { setAppLoader } from '../../../../../redux/common/CommonActions';
import { formateWithMoment } from '../../../../../utilz';
import FilterCard from '../../../../components/Cards/FilterCard';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import { LOSS_AND_DAMAGE_FILTER_TYPES } from '../../constants';
import { fetchClaimsExport } from '../data-provider/claims-export';

const normalizeExportData = (data) =>
  data.map((row) => ({
    // TODO: move to react-query
    ...row,
    claimCloseDate: convertFromESTToLocal(row.claimCloseDate),
    createdOn: convertFromESTToLocal(row.createdOn),
    dateFiled: convertFromESTToLocal(row.dateFiled),
    modifiedOn: convertFromESTToLocal(row.modifiedOn),
  }));

const LADExportForm = ({ form, setExportModal, isSelectAll }) => {
  const { filters, selectedClaims, filterType } = useSelector((state) => state.lossAndDamage);
  const dispatch = useDispatch();

  const handleSelectAll = (data, value) => {
    data.forEach(({ name }) => {
      form.setFieldValue(name, value);
    });
  };

  const handleSubmit = async (formData) => {
    try {
      const selectedColumns = LAD_CLAIM_EXPORT_FIELDS?.filter((row) => formData[row.name]);

      if (!selectedColumns?.length) {
        showNotification({
          message: 'Please select minimum one field to export the claims details!',
          type: NOTIFICATION_TYPES.ERROR,
        });
        return false;
      }
      dispatch(setAppLoader(true));
      const claimStatus = filters.claimStatus.join(',');
      const startDate = filters.startDate ? formateWithMoment(filters.startDate, 'YYYY-MM-DD') : undefined;
      const endDate = filters.endDate ? formateWithMoment(filters.endDate, 'YYYY-MM-DD') : undefined;
      const validClaimKeys = isSelectAll
        ? []
        : selectedClaims?.filter((key) => key != null && key !== undefined && key !== '')?.join(',') || undefined;
      const isAdvanceSearch = filterType === LOSS_AND_DAMAGE_FILTER_TYPES.ADVANCED;
      const daysOld = filters.daysOld ? parseInt(filters.daysOld, 10) : undefined;
      const data = await fetchClaimsExport({
        ...filters,
        claimNo: validClaimKeys,
        claimStatus,
        daysOld,
        endDate,
        IsAdvanceSearch: isAdvanceSearch,
        startDate,
      });
      const normalizedData = normalizeExportData(data);
      await generateExportValuesWithFields(selectedColumns, normalizedData);
      showNotification({
        message: 'Successfully exported claims details.',
        type: NOTIFICATION_TYPES.SUCCESS,
      });
      if (setExportModal) {
        setExportModal(false);
      }
    } catch {
      showNotification({
        message: 'Failed to export!',
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const generateExportValuesWithFields = (paramColumns, data) =>
    new Promise(
      (() => async (resolve, reject) => {
        try {
          let columns = paramColumns;
          const rows = [];
          data.forEach((row) => {
            const arr = [];
            columns.forEach(({ name }) => {
              if (Object.hasOwn(row, name)) {
                arr.push(row[name]);
              } else {
                arr.push('');
              }
            });

            rows.push(arr);
          });
          columns = columns.map(({ label, name }) => ({
            name: label.length > 0 ? label : '---',
            value: name,
          }));
          await exportLossAndDamageClaimsToExcel(columns, rows);
          resolve(true);
        } catch (error) {
          reject(error);
        }
      })(),
    );

  const formDataValues = Form.useWatch(null, form);

  const isSelectedAll = useMemo(() => {
    if (formDataValues) {
      const selectedArrays = [];
      for (const key in formDataValues) {
        if (formDataValues[key]) {
          selectedArrays.push(true);
        }
      }
      return selectedArrays.length === LAD_CLAIM_EXPORT_FIELDS.length;
    }
    return false;
  }, [formDataValues]);

  return (
    <Form onFinish={handleSubmit} className="h-100 custom-scroll-bar" form={form}>
      <FilterCard
        showBorder
        title={
          <FormCheckBox
            checkBoxClass="normal-checkbox"
            label="Select All"
            className="m-0"
            checked={isSelectedAll}
            onChange={(e) => {
              handleSelectAll(LAD_CLAIM_EXPORT_FIELDS, e.target.checked);
            }}
          />
        }
        className="mb-20"
      >
        <div className="px-15 pt-15">
          <Row gutter={15}>
            {LAD_CLAIM_EXPORT_FIELDS.map((row) => (
              <Col sm={6} key={row.name}>
                <FormCheckBox checkBoxClass="normal-checkbox" label={row.label} name={row.name} className="mb-15" />
              </Col>
            ))}
          </Row>
        </div>
      </FilterCard>
    </Form>
  );
};

export default LADExportForm;
